import { useState } from "react";
import axios from "axios";
import { HeartHandshake, Send, Loader2, Leaf, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import ComplianceFooter from "@/components/silentscan/ComplianceFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Instant AI Coping & Response Box — shown when a mood scan indicates
// elevated stress/anxiety. Responses are non-clinical and hedged.
export default function CopingBox({ stressLevel, score }) {
  const [phrase, setPhrase] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const send = async (e) => {
    e?.preventDefault();
    const text = phrase.trim();
    if (!text || loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/wellness/checkin`, {
        phrase: text,
        stress_level: stressLevel || null,
        score: score ?? null,
      });
      setResult(data);
    } catch (err) {
      toast.error("The check-in companion is unavailable right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="coping-box"
      className="mt-10 rounded-3xl border border-cyan-400/25 bg-gradient-to-br from-cyan-500/[0.07] to-[#0B3D91]/15 p-6"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center shrink-0">
          <HeartHandshake className="w-5 h-5 text-cyan-300" />
        </div>
        <div>
          <div className="font-display text-lg font-semibold text-white">
            Wellness Check-in Chat Box
          </div>
          <p className="mt-1 text-xs sm:text-sm text-zinc-400 leading-relaxed">
            Your check-in suggests things may feel a little heavy right now. If
            you'd like, put it into words below — you'll get a gentle,
            non-clinical reflection with a few possibilities to try.
          </p>
        </div>
      </div>

      <form onSubmit={send} className="mt-5">
        <div className="flex items-center gap-2 rounded-2xl bg-black/40 border border-white/10 focus-within:border-cyan-400/40 px-4 py-3 transition-colors">
          <input
            data-testid="coping-input"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            maxLength={500}
            placeholder="e.g., Work has been nonstop and my mind won't switch off…"
            className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-zinc-600"
            disabled={loading}
          />
          <button
            data-testid="coping-send-btn"
            type="submit"
            disabled={loading || !phrase.trim()}
            className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center shadow-[0_0_15px_rgba(0,229,255,0.4)] disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:scale-105 shrink-0"
            aria-label="Send"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            ) : (
              <Send className="w-4 h-4 text-white" />
            )}
          </button>
        </div>
      </form>

      {result && (
        <div data-testid="coping-response" className="mt-5 rounded-2xl bg-black/40 border border-white/10 p-5">
          <p className="text-sm text-zinc-200 leading-relaxed">{result.reply}</p>
          {result.suggestions?.length > 0 && (
            <div className="mt-4 space-y-2">
              {result.suggestions.map((s, i) => (
                <div
                  key={i}
                  data-testid={`coping-suggestion-${i}`}
                  className="flex items-start gap-2.5 text-xs text-zinc-300 leading-relaxed"
                >
                  <Leaf className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                  {s}
                </div>
              ))}
            </div>
          )}
          <button
            data-testid="coping-reset-btn"
            onClick={() => {
              setResult(null);
              setPhrase("");
            }}
            className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-cyan-300 hover:text-cyan-200 transition-colors"
          >
            <RefreshCcw className="w-3 h-3" />
            Share something else
          </button>
        </div>
      )}

      <ComplianceFooter variant="issue" />
    </div>
  );
}
